import { z } from "zod";

/**
 * Location validation schemas
 * Backend-aligned schemas that ensure type safety with generated API types
 */

/**
 * Backend-aligned location type enum (matches OpenAPI exactly)
 */
export const locationTypeSchema = z.enum(["Plant", "Quarry", "Pit"]);

/**
 * Location name validation schema (specialized)
 * Note: Backend enforces max 30 characters
 */
export const locationNameSchema = z
  .string()
  .min(2, "Location name must be at least 2 characters long")
  .max(30, "Location name must be at most 30 characters")
  .regex(
    /^[a-zA-Z0-9\s\-',.()&]+$/,
    "Location name contains invalid characters",
  )
  .trim();

/**
 * Country validation schema
 */
export const countrySchema = z
  .string()
  .min(1, "Country is required")
  .max(100, "Country must be less than 100 characters")
  .trim();

/**
 * State validation schema
 */
export const stateSchema = z
  .string()
  .min(1, "State/Province is required")
  .max(100, "State/Province must be less than 100 characters")
  .trim();

/**
 * City validation schema
 */
export const citySchema = z
  .string()
  .min(1, "City is required")
  .max(100, "City must be less than 100 characters")
  .trim();

/**
 * Address validation schema.
 * OpenAPI LocationCreate/Update treat address as optional/nullable; the create
 * form still requires it for UX. Use `.optional()` / `.nullable()` at the
 * composition site when building partial payloads.
 */
export const addressSchema = z
  .string()
  .min(1, "Address is required")
  .max(500, "Address must be less than 500 characters")
  .trim();

/**
 * ZIP code validation schema.
 * OpenAPI LocationCreate/Update treat zip_code as optional/nullable; the create
 * form still requires it for UX.
 */
export const zipCodeSchema = z
  .string()
  .min(1, "ZIP/Postal code is required")
  .max(20, "ZIP/Postal code must be less than 20 characters")
  .regex(/^[a-zA-Z0-9\s-]+$/, "ZIP code contains invalid characters")
  .trim();

/**
 * Description validation schema
 */
export const descriptionSchema = z
  .string()
  .max(1000, "Description must be less than 1000 characters")
  .trim()
  .optional();

/**
 * Schema for creating a new location (backend-aligned with OpenAPI LocationCreate).
 *
 * OpenAPI also accepts optional aggregate_assignments, lab_ids, and
 * user_permissions for atomic create-with-assignments. The create modal
 * collects those in later panels and applies them after create via
 * submitFacilitySetup (sequential assigns); they are omitted from this
 * form schema (details only). See LocationCreate.
 *
 * Note: client_id is required here for the server action; the backend receives
 * it as a query param.
 */
export const locationCreateSchema = z.object({
  name: locationNameSchema,
  location_type: locationTypeSchema,
  country: countrySchema,
  state: stateSchema,
  city: citySchema,
  address: addressSchema,
  zip_code: zipCodeSchema,
  description: descriptionSchema,
  client_id: z.string().uuid("Invalid client ID"),
});

/**
 * Schema for updating a location
 */
export const locationUpdateSchema = z
  .object({
    name: locationNameSchema.optional(),
    location_type: locationTypeSchema.optional(),
    country: countrySchema.optional(),
    state: stateSchema.optional(),
    city: citySchema.optional(),
    address: addressSchema.optional(),
    zip_code: zipCodeSchema.optional(),
    description: descriptionSchema,
  })
  .refine(
    (data) => Object.keys(data).length > 0,
    "At least one field must be provided for update",
  );

/**
 * Schema for location filters
 */
export const locationFiltersSchema = z.object({
  location_type: z.array(locationTypeSchema).optional(),
  country: z.array(z.string()).optional(),
  state: z.array(z.string()).optional(),
  city: z.array(z.string()).optional(),
  has_aggregates: z.boolean().optional(),
  has_samples: z.boolean().optional(),
});

// Type inference for location schemas
export type LocationCreateForm = z.infer<typeof locationCreateSchema>;
export type LocationUpdateForm = z.infer<typeof locationUpdateSchema>;
export type LocationFiltersForm = z.infer<typeof locationFiltersSchema>;
export type LocationType = z.infer<typeof locationTypeSchema>;

// Aliases for actions
export const createLocationSchema = locationCreateSchema;
export const updateLocationSchema = locationUpdateSchema;
